import { Box, Button, Stack, Typography } from '@mui/material'
import get from 'lodash/get'
import React, { createRef, RefObject, useEffect, useRef, useState } from 'react'
import BaseReactPlayer, { BaseReactPlayerProps } from 'react-player/base'
import { Icons } from 'utoniq-ui'

import { ArtistPostVideoFragment } from '@/clients/utoniq-core/schema'
import { Limited24hTag } from '@/components/organisms/post/PostBox'
import { useMessage, useRouter, useTranslation } from '@/core'
import { individualDisplayResponsive } from '@/core/individual'
import { useSettingUserInfoModal } from '@/core/membership/useSettingUserInfoModal'

import { MembershipRecommendBox } from './MembershipRecommendBox'
import { RequiredMembershipRecommendBox } from './RequiredMembershipRecommendBox'
import { ScrollView } from './ScrollView'
import { ShortVideoPlayer } from './ShortVideoPlayer'
import { ThumbnailImage } from './ThumbnailImage'

export const ShortVideoContent = ({
  posts,
  isMembershipPersonalInformationRequired,
  initialIndex,
  muted,
  defaultMute,
  onFetchMore,
  onLike,
  onReportArtist,
  onBlockUser,
  onClose,
  maxWidth = '600px',
}: {
  posts: ArtistPostVideoFragment[]
  isMembershipPersonalInformationRequired: boolean
  initialIndex: number
  muted: boolean
  defaultMute: () => void
  onFetchMore: () => void
  onLike: (postId: string, isLike: boolean, likeCount: number) => Promise<void>
  onReportArtist?: (
    postId: string,
    artistId: string,
    accountId: string
  ) => Promise<void>
  onBlockUser: (id: string) => Promise<void | boolean>
  onClose: () => void
  maxWidth?: string
}) => {
  const { t } = useTranslation('artistPostVideos')
  const { routerPages, generatePath, router } = useRouter<{ postId: string }>()
  const { showMessage } = useMessage()
  const { t: tmembershipUserInfo } = useTranslation('membershipUserInfo')
  const { showModal } = useSettingUserInfoModal()
  // NOTE 動画プレイヤー制御用のRef
  const videoRefs = useRef<RefObject<BaseReactPlayer<BaseReactPlayerProps>>[]>(
    []
  )
  posts.forEach((_, i) => {
    videoRefs.current[i] = createRef<BaseReactPlayer<BaseReactPlayerProps>>()
  })

  // NOTE 画面スクロール制御用のRef
  const wrapperRefs = useRef<RefObject<HTMLDivElement>[]>([])
  posts.forEach((_, i) => {
    wrapperRefs.current[i] = createRef<HTMLDivElement>()
  })
  const scrollViewRef = useRef<HTMLDivElement>(null)

  const [playingVideoIndex, setPlayingVideoIndex] = useState<number | 'paused'>(
    initialIndex
  )
  const [showingVideoIndex, setShowingVideoIndex] =
    useState<number>(initialIndex)

  const displayResponsive = individualDisplayResponsive()

  useEffect(() => {
    const scrollView = scrollViewRef.current
    if (!scrollView) return
    const observers: IntersectionObserver[] = []

    posts.forEach((_, i) => {
      const wrapper = wrapperRefs.current[i].current
      if (!wrapper) return

      const callback: IntersectionObserverCallback = entries => {
        entries.forEach(entry => {
          if (entry.intersectionRatio === 1.0) {
            posts.forEach((_, index) => {
              const v = videoRefs.current[index].current
              const w = wrapperRefs.current[index].current
              if (!w) return
              if (w === entry.target) {
                setPlayingVideoIndex(index)
                setShowingVideoIndex(index)

                // 末尾まで残り少なくなったら次の分をロードする
                // NOTE 4件前になったら追加ロード
                if (index > posts.length - 4) {
                  onFetchMore()
                }
              } else if (get(v, 'player.isReady', false)) {
                // In some cases, the player is not ready, but the seekTo method is called, leading to errors.
                // Therefore, it is necessary to check whether the player is ready
                v?.seekTo(0)
              }
            })
          }
        })
      }
      const options = { root: scrollView, threshold: 1 }
      const observer = new IntersectionObserver(callback, options)
      observer.observe(wrapper)
      observers.push(observer)
    })

    return () => {
      observers.forEach(observer => observer.disconnect())
    }
  }, [posts.length])

  useEffect(() => {
    // NOTE サムネイルタップ時に初期位置をその動画までスクロールさせる（これがないと、どのサムネイルをタップしても一番先頭の動画が表示される）
    const wrapper = wrapperRefs.current[initialIndex]?.current
    if (!wrapper) return
    wrapper.scrollIntoView()
  }, [initialIndex])

  return (
    <ScrollView ref={scrollViewRef}>
      {posts.map((post, idx) => {
        return (
          <Box
            key={post.id}
            sx={{
              width: '100%',
              maxWidth: maxWidth,
              height: '100dvh',
              position: 'relative',
              scrollSnapStop: 'always',
              scrollSnapAlign: 'start',
              ...(displayResponsive
                ? {
                    display: 'flex',
                    justifyContent: 'center',
                    alignItems: 'center',
                  }
                : {}),
            }}
            ref={wrapperRefs.current[idx]}
          >
            {post.isStory && (
              <Limited24hTag
                sx={{ position: 'absolute', top: '8px', right: '8px' }}
              />
            )}
            {post.isAccessible ? (
              post.isPremium &&
              isMembershipPersonalInformationRequired &&
              post.artist?.patron?.isPersonalInformationRequired ? (
                <>
                  <ThumbnailImage
                    src={post.videoThumbnail?.blurUri || ''}
                    alt="thumbnail"
                    withBorderRadius
                  />
                  <RequiredMembershipRecommendBox
                    title={
                      <Stack alignItems="center" spacing={3} pb={3}>
                        <Icons.Info
                          sx={{ color: 'error.main', width: 40, height: 40 }}
                        />
                        <Typography variant="h5">
                          {tmembershipUserInfo('noticeRegisterModalTitleV2')}
                        </Typography>
                      </Stack>
                    }
                    content={tmembershipUserInfo('noticeRegisterModalDescV2')}
                    buttons={
                      <Button
                        sx={{ minWidth: 212 }}
                        variant="contained"
                        color="primary"
                        size="large"
                        onClick={() => showModal()}
                      >
                        {tmembershipUserInfo('enterYourInfo', '入力する')}
                      </Button>
                    }
                  />
                </>
              ) : // NOTE 現在画面に表示されている投稿の前後1つだけvideoを生成し、残りはサムネ表示とする
              showingVideoIndex - 1 <= idx && idx <= showingVideoIndex + 1 ? (
                <ShortVideoPlayer
                  key={post.id}
                  post={post}
                  videoRef={videoRefs.current[idx]}
                  playing={playingVideoIndex === idx}
                  muted={muted}
                  maxWidth={displayResponsive ? maxWidth : undefined}
                  action={{
                    play: () => {
                      setPlayingVideoIndex(idx)
                    },
                    pause: () => {
                      setPlayingVideoIndex('paused')
                    },
                    togglePlay: () => {
                      setPlayingVideoIndex(prev =>
                        prev === 'paused' ? idx : 'paused'
                      )
                    },
                    seek: time => {
                      videoRefs.current[idx].current?.seekTo(time)
                    },
                    defaultMute,
                    onLike: async () => {
                      await onLike(post.id, post.isLike, post.likeCount)
                    },
                    onReportArtist: async () => {
                      if (!onReportArtist) return

                      await onReportArtist(
                        post?.id ?? '',
                        post?.artist?.id ?? '',
                        post?.artist?.formattedAccountId ?? ''
                      )
                    },
                    onBlockArtist: async () => {
                      await onBlockUser(post?.artistId ?? '')
                      showMessage(t('blockArtistSuccess'), 'success')
                      router.push(
                        generatePath(routerPages.ArtistTop, {
                          artistId: post?.artistId,
                        })
                      )
                    },
                    onClose: onClose,
                  }}
                />
              ) : (
                <ThumbnailImage
                  src={post.videoThumbnail?.uri || ''}
                  alt="thumbnail"
                  withBorderRadius
                />
              )
            ) : (
              <>
                <ThumbnailImage
                  src={post.videoThumbnail?.blurUri || ''}
                  alt="thumbnail"
                  withBorderRadius
                />
                <MembershipRecommendBox
                  artistId={post.artistId}
                  membershipRecommendTitle={t('premiumRecommendTitle')}
                  membershipRecommendText={t('premiumRecommendText', {
                    artistName: post.artist.displayName,
                    membershipName:
                      post.planId && post.plan?.planTitle
                        ? post.plan?.planTitle
                        : 'プレミアムメンバー',
                  })}
                  recommendButtonText={t('premiumRecommendButton')}
                />
              </>
            )}
          </Box>
        )
      })}
    </ScrollView>
  )
}
